import { createContext, useContext, useState, ReactNode, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { createRequestId, recordAuthPageAction, startTimer } from "../utils/perfTelemetry";

export type Tenant = {
  id: string;
  displayname: string;
  slug: string;
  // Optional fields that are present on tenant objects returned from the API
  name?: string;
  email?: string;
  phone?: string;
};

type AuthContextType = {
  user: any | null;
  setUser: (userDetails: any) => void;
  tenantList: Tenant[];
  setTenantList: (tenants: Tenant[]) => void;
  login: (userDetails: any) => void;
  logout: () => void;
};
const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<any | null>(null);
  const [tenantList, setTenantList] = useState<Tenant[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    const elapsed = startTimer();
    const requestId = createRequestId();
    const storedUser = localStorage.getItem("userData");
    const storedTenants = localStorage.getItem("tenantList");
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
        recordAuthPageAction({
          action: "session_restore",
          outcome: "success",
          duration_ms: elapsed(),
          requestId,
          status: 200,
        });
      } catch {
        recordAuthPageAction({
          action: "session_restore",
          outcome: "failure",
          duration_ms: elapsed(),
          requestId,
          failure_stage: "parse_user_data",
          status: 0,
        });
      }
    } else {
      recordAuthPageAction({
        action: "session_restore",
        outcome: "not_found",
        duration_ms: elapsed(),
        requestId,
        status: 200,
      });
    }
    if (storedTenants) {
      setTenantList(JSON.parse(storedTenants));
    }
  }, []);

  const login = (userDetails: any) => {
    setUser(userDetails);
    localStorage.setItem("userData", JSON.stringify(userDetails));
  };

  const handleSetTenantList = useCallback((tenants: Tenant[]) => {
    setTenantList(tenants);
    // localStorage.setItem("tenantList", JSON.stringify(tenants));
  }, []);

  const updateUser = (userDetails: any) => {
    setUser(userDetails);
    localStorage.setItem("userData", JSON.stringify(userDetails));
  };

  const logout = () => {
    const elapsed = startTimer();
    const requestId = createRequestId();
    setUser(null);
    setTenantList([]);
    localStorage.clear();
    sessionStorage.clear();
    recordAuthPageAction({
      action: "logout",
      outcome: "success",
      duration_ms: elapsed(),
      requestId,
      status: 200,
    });
    navigate("/login");
  };

  return (
    <AuthContext.Provider value={{ user, setUser: updateUser, tenantList, setTenantList: handleSetTenantList, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};
